import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Users, Package, Calendar } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const UsageHistory = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadHistory();
  }, [tenantId, period]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Load tenant
      const tenantResponse = await axios.get(`${API_URL}/api/super-admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenant(tenantResponse.data.tenant);
      
      // Load usage history
      const historyResponse = await axios.get(
        `${API_URL}/api/super-admin/tenants/${tenantId}/usage-history?days=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(historyResponse.data.history || []);
    } catch (error) {
      console.error('Load history error:', error);
      toast.error('Failed to load usage history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/super-admin/tenants/${tenantId}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Usage History</h1>
            <p className="text-gray-500 mt-1">{tenant?.company_name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={period === 7 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriod(7)}
          >
            7 Days
          </Button>
          <Button
            variant={period === 30 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriod(30)}
          >
            30 Days
          </Button>
          <Button
            variant={period === 90 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriod(90)}
          >
            90 Days
          </Button>
        </div>
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Drivers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tenant?.current_usage?.drivers_count || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Total drivers created</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Package className="h-4 w-4" />
              Deliveries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tenant?.current_usage?.deliveries_count || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Total deliveries processed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Growth
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{history.length > 1 ? Math.round(((history[history.length - 1]?.total || 0) - (history[0]?.total || 0)) / (history[0]?.total || 1) * 100) : 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Last {period} days</p>
          </CardContent>
        </Card>
      </div>

      {/* Usage Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Trend</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="drivers" stroke="#3b82f6" name="Drivers" />
                <Line type="monotone" dataKey="deliveries" stroke="#10b981" name="Deliveries" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No usage data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {history.slice().reverse().slice(0, 10).map((day) => (
              <div key={day.date} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium">{new Date(day.date).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-6 text-sm">
                  <div className="text-blue-600">
                    <Users className="h-3 w-3 inline mr-1" />
                    {day.drivers || 0} drivers
                  </div>
                  <div className="text-green-600">
                    <Package className="h-3 w-3 inline mr-1" />
                    {day.deliveries || 0} deliveries
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsageHistory;
